import axios, { type AxiosError, type AxiosRequestConfig } from "axios";

import { clearTokens, getAccessToken, getTokens, redirectToLogin, setTokens } from "@/lib/auth";
import { Config } from "@/lib/config";
import { captureException } from "@/lib/errors";
import type { RefreshTokenResponse } from "./types";

const API_BASE_URL = Config.api_base_url;
const TENANT_REFRESH_URL = Config.tenant_refresh_url;
const SAFE_QUERY_PARAMS = new Set([
  "tenant",
  "page",
  "limit",
  "per_page",
  "date_from",
  "date_to",
  "start_date",
  "end_date",
  "status",
  "type",
  "category",
  "sort_by",
  "order",
  "include",
  "fields",
  "format",
]);

interface RetryableAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

const AUTH_ENDPOINTS = [
  "/tenant-login",
  "/tenant-refresh",
  "/tenant-verify-code",
  "/tenant-resend-verification-code",
  "/forgot-password",
  "/reset-password",
];

const isAuthEndpoint = (url?: string): boolean => AUTH_ENDPOINTS.some((endpoint) => url?.includes(endpoint));

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use(
  (config) => {
    config.headers = config.headers || {};

    if (isAuthEndpoint(config.url)) {
      return config;
    }

    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

const rejectAfterAuthFailure = (cause: unknown) => {
  clearTokens();
  redirectToLogin();
  return Promise.reject(cause);
};

const handleTokenRefresh = async (originalRequest: RetryableAxiosRequestConfig) => {
  const tokens = getTokens();
  if (tokens?.refresh) {
    try {
      const refreshResponse = await axios.post<RefreshTokenResponse>(`${TENANT_REFRESH_URL}`, {
        refresh: tokens.refresh,
      });

      const newTokens = {
        access: refreshResponse.data.access,
        refresh: tokens.refresh,
      };

      setTokens(newTokens);

      if (originalRequest.headers) {
        originalRequest.headers["Authorization"] = `Bearer ${newTokens.access}`;
      }

      return api(originalRequest);
    } catch (refreshError) {
      return rejectAfterAuthFailure(refreshError);
    }
  } else {
    return rejectAfterAuthFailure(new Error("No refresh token available"));
  }
};

const normalizePathSegments = (path: string): string => path.replace(/\/\d+/g, "/:id");

const sanitizeUrl = (url: string | undefined): string => {
  if (!url) return "unknown";

  try {
    const urlObj = new URL(url, "http://localhost");
    const path = urlObj.pathname;
    const searchParams = urlObj.searchParams;

    const sanitizedParams = new URLSearchParams();
    for (const [key, value] of searchParams.entries()) {
      if (SAFE_QUERY_PARAMS.has(key.toLowerCase())) {
        sanitizedParams.set(key, value);
      } else {
        sanitizedParams.set(key, "[REDACTED]");
      }
    }

    const queryString = sanitizedParams.toString();
    return queryString ? `${path}?${queryString}` : path;
  } catch {
    return url.split("?")[0].split("#")[0];
  }
};

const captureAxiosError = (error: AxiosError, originalRequest: RetryableAxiosRequestConfig) => {
  const method = originalRequest?.method?.toUpperCase() || "UNKNOWN";
  const endpoint = sanitizeUrl(originalRequest?.url);
  const normalizedEndpoint = normalizePathSegments(endpoint);
  const statusCode = error.status || "NO_RESPONSE";

  const errorTitle = `${method} ${normalizedEndpoint} - ${statusCode}`;

  captureException(error, {
    fingerprint: [method, normalizedEndpoint, String(statusCode)],
    tags: {
      operation: "api_request_failed",
      endpoint: normalizedEndpoint,
      method: method,
      status_code: statusCode,
    },
    extra: {
      request_config: {
        method: originalRequest?.method,
        baseURL: originalRequest?.baseURL,
      },
      response_data: error.response?.data,
      error_message: error.message,
      actual_endpoint: endpoint,
    },
    contexts: {
      api_error: {
        title: errorTitle,
        url: normalizedEndpoint,
        http_method: method,
        http_status: statusCode,
      },
    },
  });
};

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableAxiosRequestConfig;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthEndpoint(originalRequest?.url)
    ) {
      originalRequest._retry = true;
      return handleTokenRefresh(originalRequest);
    }

    captureAxiosError(error, originalRequest);

    return Promise.reject(error);
  },
);

export default api;
